import { useAuthStore } from '../../stores/useAuthStore';
import type { AuthError } from '../../stores/useAuthStore';
import styles from './LoginScreen.module.css';
import { useState } from 'react';

function errorMessage(error: AuthError): string {
  switch (error.code) {
    case 'INVITE_REQUIRED':
      return 'An invite code is required to sign in.';
    case 'INVALID_INVITE_CODE':
      return 'Invite code is invalid. Please request a valid code.';
    case 'WALLET_NOT_FOUND':
      return 'Natos Wallet was not detected. Please install the Natos Wallet browser extension and reload this page.';
    case 'WALLET_LOCKED':
      return 'Natos Wallet is locked. Please unlock your wallet and try again.';
    case 'CONNECTION_REJECTED':
      return 'Wallet connection was rejected. Please approve the connection request in Natos Wallet.';
    case 'SIGNATURE_REJECTED':
      return 'Sign-in was cancelled. You must sign the authentication challenge to continue.';
    case 'SIGNATURE_INVALID':
      return 'The signature could not be verified. Please try again.';
    case 'CHALLENGE_EXPIRED':
      return 'The authentication challenge has expired. Please try again.';
    case 'NO_ACCOUNT':
      return 'No account found in Natos Wallet. Please create or import an account first.';
    default:
      return error.message;
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'detecting-wallet': return 'Detecting Natos Wallet...';
    case 'awaiting-connect': return 'Waiting for wallet connection...';
    case 'awaiting-signature': return 'Waiting for signature...';
    case 'verifying': return 'Verifying authentication...';
    default: return '';
  }
}

export function LoginScreen() {
  const { status, error, beginLogin, clearError } = useAuthStore();
  const [inviteCode, setInviteCode] = useState('');

  const isLoading = status === 'detecting-wallet' || status === 'awaiting-connect'
    || status === 'awaiting-signature' || status === 'verifying';

  const handleSignIn = () => {
    if (error) clearError();
    beginLogin(inviteCode);
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.logoSection}>
          <img src="/logo.png" alt="Rivellum" className={styles.logo} />
          <h1 className={styles.title}>Rivellum Console</h1>
          <p className={styles.subtitle}>
            Secure sign-in with Natos Wallet
          </p>
        </div>

        <div className={styles.description}>
          <p>
            Browser-based development environment for Rivellum. Sign in with Natos Wallet.
          </p>
        </div>

        <div className={styles.inviteFieldWrap}>
          <label htmlFor="invite-code" className={styles.inviteLabel}>Invite Code</label>
          <input
            id="invite-code"
            className={styles.inviteInput}
            type="text"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            placeholder="Enter invite code"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            disabled={isLoading}
          />
        </div>

        {error && (
          <div className={styles.errorBanner}>
            <span className={styles.errorIcon}>!</span>
            <span className={styles.errorText}>{errorMessage(error)}</span>
          </div>
        )}

        {isLoading && (
          <div className={styles.loadingBanner}>
            <div className={styles.spinner} />
            <span>{statusLabel(status)}</span>
          </div>
        )}

        <button
          className={styles.signInButton}
          onClick={handleSignIn}
          disabled={isLoading}
        >
          {isLoading ? 'Connecting...' : 'Sign in with Natos Wallet'}
        </button>

        <div className={styles.securityNote}>
          <span className={styles.shieldIcon}>&#x1F6E1;</span>
          Post-quantum authenticated. Your session is secured by challenge-response signing.
        </div>
      </div>

      <div className={styles.footer}>
        <span>Rivellum Console</span>
        <span className={styles.dot}>&middot;</span>
        <a href="https://console.rivellum.network" target="_blank" rel="noopener noreferrer">
          Home
        </a>
        <span className={styles.dot}>&middot;</span>
        <a href="https://portal.rivellum.network/docs" target="_blank" rel="noopener noreferrer">
          Documentation
        </a>
      </div>
    </div>
  );
}
